/**
 * The notification list (spec §5): Needs you, Today and Earlier with their
 * rows, and Mark all read / Clear all on the first section after Needs you.
 * The hub renders its sections with these pieces; the wall's dropdown
 * renders the whole list.
 */
import type { ReactNode } from "react";
import { PocketSection } from "@/components/ui";
import type { HubSection } from "@/components/panels/search-modal/hub-types";
import {
  ApprovalRow,
  NotificationListActions,
  NotificationRow,
} from "./notification-row";
import type {
  NotificationActions,
  NotificationItem,
} from "./notification-item";

export function NotificationItemRow({
  item,
  query,
  selected = false,
  onOpen,
  actions,
}: {
  item: NotificationItem;
  query: string;
  selected?: boolean;
  onOpen: () => void;
  actions: NotificationActions;
}) {
  return item.type === "notification" ? (
    <NotificationRow
      row={item.row}
      query={query}
      selected={selected}
      onOpen={onOpen}
      onClear={actions.clearRow}
    />
  ) : (
    <ApprovalRow
      approval={item.approval}
      query={query}
      selected={selected}
      onOpen={onOpen}
      onDecide={actions.decide}
    />
  );
}

/** A list section; the one that asks for them carries the list actions. */
export function NotificationSection({
  section,
  actions,
  testId,
  children,
}: {
  section: HubSection;
  actions: NotificationActions;
  testId: string;
  children: ReactNode;
}) {
  return (
    <PocketSection
      title={section.title ?? undefined}
      testId={testId}
      action={
        section.notificationActions ? (
          <NotificationListActions
            onMarkAllRead={actions.markAllRead}
            onClearAll={actions.clearAll}
          />
        ) : undefined
      }
    >
      {children}
    </PocketSection>
  );
}

/** The notification sections as one list (`notificationSections`). */
export function NotificationList({
  sections,
  query,
  actions,
  onOpen,
}: {
  sections: readonly HubSection[];
  query: string;
  actions: NotificationActions;
  onOpen: (item: NotificationItem) => void;
}) {
  return sections.map((section) => (
    <NotificationSection
      key={section.id}
      section={section}
      actions={actions}
      testId={`notification-section-${section.id}`}
    >
      {section.items.map((item) =>
        item.type === "notification" || item.type === "approval" ? (
          <NotificationItemRow
            key={item.key}
            item={item}
            query={query}
            onOpen={() => onOpen(item)}
            actions={actions}
          />
        ) : null,
      )}
    </NotificationSection>
  ));
}
